import React, { useState } from 'react';
import { COUNTRY_PHONE_DATA } from '../pages/Passes';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import toast from 'react-hot-toast';

export default function GuestDetailsModal({ pass, onClose, onUpdate }) {
  const { faculty } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    guestName: pass.guestName || '',
    guestEmail: pass.guestEmail || '',
    personalEmail: pass.personalEmail || '',
    guestPhone: pass.guestPhone || '',
    country: pass.country || 'India',
    organization: pass.organization || '',
    guestDepartment: pass.guestDepartment || '',
    industry: pass.industry || '',
    address: pass.address || '',
    linkedinId: pass.linkedinId || '',
    profession: pass.profession || ''
  });

  if (!pass) return null;

  const isAdmin = ['iqac', 'super_admin'].includes(faculty?.role);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const eventEnd = pass.event?.isMultiDay && pass.event?.endDate ? new Date(pass.event.endDate) : new Date(pass.event?.date);
  const endDay = new Date(eventEnd.getFullYear(), eventEnd.getMonth(), eventEnd.getDate());
  const isLocked = (today > endDay) && !isAdmin;
  const canEdit = faculty && !isLocked;

  const handleSave = async () => {
    setLoading(true);
    try {
      const res = await api.put(`/passes/${pass._id}`, formData);
      if (res.data.success) {
        toast.success('Guest details updated');
        setIsEditing(false);
        if (onUpdate) onUpdate(res.data.pass);
        // We might want to reload the page or update parent state
        // For now, let's just close or let the user see the updated details
        window.location.reload(); 
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update guest');
    } finally {
      setLoading(false);
    }
  };

  const formatPhone = (phone, country) => {
    if (!phone) return '—';
    const data = COUNTRY_PHONE_DATA[country];
    
    const splitDigits = (s) => {
      const d = s.replace(/\D/g, '');
      if (d.length === 10) return `${d.slice(0, 5)} ${d.slice(5)}`;
      if (d.length === 9) return `${d.slice(0, 4)} ${d.slice(4)}`;
      return s;
    };

    if (data) {
      const codeStr = data.code.replace('+', '');
      let cleanPhone = phone.replace(/\D/g, '');
      if (cleanPhone.startsWith(codeStr)) {
        cleanPhone = cleanPhone.slice(codeStr.length);
      }
      return `${data.code} ${splitDigits(cleanPhone)}`;
    }
    return splitDigits(phone);
  };

  const formatLinkedIn = (id) => {
    if (!id) return null;
    let clean = id.trim();
    if (clean.startsWith('http')) return clean;
    if (clean.startsWith('linkedin.com')) return `https://${clean}`;
    if (clean.startsWith('en.wikipedia.org') || clean.startsWith('wikipedia.org')) return `https://${clean}`;
    if (clean.includes('wikipedia.org')) return clean.startsWith('http') ? clean : `https://${clean}`;
    return `https://www.linkedin.com/in/${clean}`;
  };

  return (
    <div className="modal-overlay" role="presentation" style={{ zIndex: 1000 }}>
      <div className="modal event-modal" style={{ maxWidth: '44rem' }}>
        <div className="modal-header">
          <div className="modal-title">{isEditing ? 'Edit Guest Details' : 'Guest Details'}</div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="modal-body">
          {isEditing ? (
            <div className="event-detail-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <div className="field">
                <label className="pass-detail-label">Full Name</label>
                <input className="form-control" value={formData.guestName} onChange={e => setFormData({...formData, guestName: e.target.value})} />
              </div>
              <div className="field">
                <label className="pass-detail-label">Profession</label>
                <input className="form-control" value={formData.profession} onChange={e => setFormData({...formData, profession: e.target.value})} />
              </div>
              <div className="field">
                <label className="pass-detail-label">Work Email</label>
                <input className="form-control" value={formData.guestEmail} onChange={e => setFormData({...formData, guestEmail: e.target.value})} />
              </div>
              <div className="field">
                <label className="pass-detail-label">Personal Email</label>
                <input className="form-control" value={formData.personalEmail} onChange={e => setFormData({...formData, personalEmail: e.target.value})} />
              </div>
              <div className="field">
                <label className="pass-detail-label">Phone</label>
                <input className="form-control" value={formData.guestPhone} onChange={e => setFormData({...formData, guestPhone: e.target.value})} />
              </div>
              <div className="field">
                <label className="pass-detail-label">Country</label>
                <input className="form-control" value={formData.country} onChange={e => setFormData({...formData, country: e.target.value})} />
              </div>
              <div className="field">
                <label className="pass-detail-label">Organisation</label>
                <input className="form-control" value={formData.organization} onChange={e => setFormData({...formData, organization: e.target.value})} />
              </div>
              <div className="field">
                <label className="pass-detail-label">Department</label>
                <input className="form-control" value={formData.guestDepartment} onChange={e => setFormData({...formData, guestDepartment: e.target.value})} />
              </div>
              <div className="field">
                <label className="pass-detail-label">Industry</label>
                <input className="form-control" value={formData.industry} onChange={e => setFormData({...formData, industry: e.target.value})} />
              </div>
              <div className="field">
                <label className="pass-detail-label">LinkedIn / Wikipedia</label>
                <input className="form-control" value={formData.linkedinId} onChange={e => setFormData({...formData, linkedinId: e.target.value})} />
              </div>
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label className="pass-detail-label">Office Address</label>
                <textarea className="form-control" value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} rows={2} />
              </div>
            </div>
          ) : (
            <>
              <div className="pass-hero-block">
                <div className="pass-hero-label">Guest name</div>
                <div className="pass-hero-code" style={{ fontSize: '1.4rem', wordBreak: 'break-word' }}>
                  {pass.guestName || '—'}
                </div>
                <div className="event-detail-subtitle">{pass.profession || '—'}</div>
              </div>

              <div className="event-detail-grid">
                <div className="pass-detail-item"><span className="pass-detail-label">Work Email</span><span className="pass-detail-value">{pass.guestEmail || '—'}</span></div>
                <div className="pass-detail-item"><span className="pass-detail-label">Personal Email</span><span className="pass-detail-value">{pass.personalEmail || '—'}</span></div>
                <div className="pass-detail-item"><span className="pass-detail-label">Mobile</span><span className="pass-detail-value">{formatPhone(pass.guestPhone, pass.country)}</span></div>
                <div className="pass-detail-item"><span className="pass-detail-label">Country</span><span className="pass-detail-value">{pass.country || '—'}</span></div>
                <div className="pass-detail-item"><span className="pass-detail-label">Organisation</span><span className="pass-detail-value">{pass.organization || '—'}</span></div>
                <div className="pass-detail-item"><span className="pass-detail-label">Department</span><span className="pass-detail-value">{pass.guestDepartment || '—'}</span></div>
                <div className="pass-detail-item"><span className="pass-detail-label">Industry</span><span className="pass-detail-value">{pass.industry || '—'}</span></div>
                <div className="pass-detail-item">
                  <span className="pass-detail-label">Social Profile</span>
                  <span className="pass-detail-value">
                    {pass.linkedinId ? (
                      <a 
                        href={formatLinkedIn(pass.linkedinId)} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        style={{ color: 'var(--navy)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: 700 }}
                      >
                        Open Profile
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.8 }}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                      </a>
                    ) : '—'}
                  </span>
                </div>
                <div className="pass-detail-item" style={{ gridColumn: '1 / -1' }}><span className="pass-detail-label">Office Address</span><span className="pass-detail-value">{pass.address || '—'}</span></div>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', padding: '1.25rem 1.5rem', borderTop: '1px solid var(--border-1)', background: 'var(--gray-50)' }}>
          {isEditing ? (
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setIsEditing(false)} disabled={loading}>
                Cancel
              </button>
              <button type="button" className="btn btn-navy" onClick={handleSave} disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <>
              {canEdit && (
                <button type="button" className="btn btn-navy" onClick={() => setIsEditing(true)}>
                  Edit Details
                </button>
              )}
              <button type="button" className="btn btn-ghost" onClick={onClose}>
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
